import {
  Text,
  Avatar,
  Badge,
  Button,
  Box,
  ThemeIcon,
  HoverCard,
} from '@mantine/core';
import { Check } from 'react-feather';
import type {
  CohortCoachWithDetails,
  CohortCoachAvailabilityStatus,
  SlotConflict,
} from '../../../services/api/cohortCoaches';
import type { SessionWithDetails } from '../../../services/api/sessions';
import classes from './CoachCard.module.css';
import { getAvatarColor, getInitialsFromParts } from '../../../utils/avatarHelpers';
import { COACH_AVAILABILITY_CONFIG } from '../../../config/statusConfig';
import { isMedleyEmail } from '../../../utils/medleyEmail';
import {
  NO_MEDLEY_EMAIL_BADGE_STYLES,
  NO_MEDLEY_EMAIL_LABEL,
} from '../../../components/coaches/noMedleyEmailBadge';
import { NeedsMedleyEmailHint } from '../../../components/coaches/NeedsMedleyEmailHint';

// Group availability data for a coach
export interface CoachGroupAvailability {
  groupId: string;
  groupName: string;
  availableSessions: number | null; // null = unknown/pending
  totalSessions: number;
  status: 'full' | 'partial' | 'unknown';
  conflicts?: SlotConflict[];
}

interface CoachCardProps {
  coach: CohortCoachWithDetails;
  maxGroups: number | null;
  isFlexibleGroups: boolean;
  groupAvailability: CoachGroupAvailability[];
  extraGroupsCount: number; // Number of groups beyond the displayed 3
  onSendReminder: () => void;
  onSelect: () => void;
  isSelected?: boolean;
  groupSessionsMap?: Record<string, SessionWithDetails[]>;
}

// Get status class for text
const getStatusClass = (status: CohortCoachAvailabilityStatus): string => {
  switch (status) {
    case 'available':
      return classes.statusAvailable;
    case 'partially_available':
      return classes.statusPartial;
    default:
      return classes.statusPending;
  }
};

// Get status label from shared config
const getStatusLabel = (status: CohortCoachAvailabilityStatus): string => {
  return COACH_AVAILABILITY_CONFIG[status]?.label ?? 'Unknown';
};


// Get pill class based on availability
const getPillClass = (status: 'full' | 'partial' | 'unknown'): string => {
  switch (status) {
    case 'full':
      return classes.groupPillFull;
    case 'partial':
      return classes.groupPillPartial;
    default:
      return classes.groupPillUnknown;
  }
};

export function CoachCard({
  coach,
  maxGroups,
  isFlexibleGroups,
  groupAvailability,
  extraGroupsCount,
  onSendReminder,
  onSelect,
  isSelected = false,
  groupSessionsMap,
}: CoachCardProps) {
  const isPending = coach.availability_status === 'pending_response';
  const avatarColor = getAvatarColor(coach.id);
  const fullName = `${coach.first_name || ''} ${coach.last_name || ''}`.trim() || coach.email;
  // MED-1355: a group lead needs a Medley email. The API refuses the
  // assignment without one, so the card does not offer it.
  const needsMedleyEmail = !isMedleyEmail(coach.email);

  // Match a conflict date to a session number from the group's sessions
  const getSessionNumber = (groupId: string, conflictDate: string): number | null => {
    const sessions = groupSessionsMap?.[groupId];
    if (!sessions) return null;
    const conflictDateStr = conflictDate.split('T')[0];
    const match = sessions.find((s) => s.scheduled_start?.split('T')[0] === conflictDateStr);
    return match?.session_number ?? null;
  };

  return (
    <Box className={`${classes.card} ${isSelected ? classes.cardSelected : ''}`}>
      {/* Selected checkmark badge */}
      {isSelected && (
        <ThemeIcon
          size={24}
          radius="xl"
          color="blue"
          className={classes.selectedBadge}
        >
          <Check size={14} />
        </ThemeIcon>
      )}

      {/* Avatar and Name Row */}
      <div className={classes.nameRow}>
        <Avatar
          size={44}
          radius="xl"
          src={coach.avatar_url}
          styles={{
            root: { backgroundColor: avatarColor },
            placeholder: { color: '#FFFFFF', backgroundColor: avatarColor },
          }}
        >
          {getInitialsFromParts(coach.first_name, coach.last_name)}
        </Avatar>
        <div>
          <div className={classes.nameText}>
            {fullName}
            {coach.coach_type === 'partner' && (
              <Badge size="xs" color="blue" variant="light" ml={6} style={{ verticalAlign: 'middle' }}>
                Partner
              </Badge>
            )}
            {needsMedleyEmail && (
              <Badge
                styles={NO_MEDLEY_EMAIL_BADGE_STYLES}
                ml={6}
                style={{ verticalAlign: 'middle' }}
              >
                {NO_MEDLEY_EMAIL_LABEL}
              </Badge>
            )}
          </div>
          <div className={classes.statusRow}>
            <span className={`${classes.statusText} ${getStatusClass(coach.availability_status)}`}>
              {getStatusLabel(coach.availability_status)}
            </span>
            {(maxGroups !== null || isFlexibleGroups) && (
              <>
                <span className={classes.statusSeparator}>|</span>
                <span className={classes.maxGroupsText}>
                  {isFlexibleGroups ? 'Flexible' : `Up to ${maxGroups} Groups`}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Group Availability Pills */}
      <div className={classes.groupPillsRow}>
        {groupAvailability.map((group) => {
          const displayText = group.availableSessions !== null
            ? `${group.groupName} ${group.availableSessions}/${group.totalSessions}`
            : `${group.groupName} —`;

          const pill = (
            <span
              className={`${classes.groupPill} ${getPillClass(group.status)}`}
            >
              {displayText}
            </span>
          );

          // Wrap partial pills with a conflict tooltip
          if (group.status === 'partial' && group.conflicts && group.conflicts.length > 0) {
            return (
              <HoverCard
                key={group.groupId}
                width="auto"
                position="bottom"
                withArrow
                shadow="md"
                openDelay={200}
                closeDelay={100}
              >
                <HoverCard.Target>{pill}</HoverCard.Target>
                <HoverCard.Dropdown
                  style={{ padding: 0, border: 'none', background: 'transparent' }}
                >
                  <div className={classes.conflictTooltip}>
                    <div className={classes.conflictHeader}>
                      <span className={classes.conflictTitle}>
                        {group.conflicts.length} Conflict{group.conflicts.length !== 1 ? 's' : ''}
                      </span>
                      <span className={classes.conflictBadge}>
                        {group.availableSessions} of {group.totalSessions} available
                      </span>
                    </div>
                    <div className={classes.conflictList}>
                      {group.conflicts.map((conflict, idx) => {
                        const sessionNum = getSessionNumber(group.groupId, conflict.date);
                        const formattedDate = new Date(conflict.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        });
                        return (
                          <div key={idx} className={classes.conflictItem}>
                            <span className={classes.conflictDate}>
                              {sessionNum ? `Session ${sessionNum} · ` : ''}{formattedDate}
                            </span>
                            {conflict.reason && (
                              <span className={classes.conflictReason}>{conflict.reason}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </HoverCard.Dropdown>
              </HoverCard>
            );
          }

          return <span key={group.groupId}>{pill}</span>;
        })}
        {extraGroupsCount > 0 && (
          <span className={classes.extraGroupsText}>+{extraGroupsCount}</span>
        )}
      </div>

      {/* Divider */}
      <hr className={classes.divider} />

      {/* Footer Action */}
      {isPending ? (
        <Button
          variant="default"
          size="xs"
          fullWidth
          className={classes.reminderButton}
          onClick={(e) => {
            e.stopPropagation();
            onSendReminder();
          }}
        >
          Send Reminder
        </Button>
      ) : needsMedleyEmail && !isSelected ? (
        <NeedsMedleyEmailHint
          coachProfileId={coach.coach_profile_id}
          coachName={fullName}
          justify="center"
        />
      ) : (
        <Text
          className={classes.selectLink}
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
        >
          {isSelected ? 'Click to deselect coach & cancel' : 'Click to select & assign'}
        </Text>
      )}
    </Box>
  );
}

export default CoachCard;
